import React, { useState } from 'react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const initialForm = {
  company_name: '',
  contact_name: '',
  email: '',
  phone: '',
  team_size: '',
  preferred_dates: '',
  message: ''
};

export const TeamInquiryDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async () => {
    if (!form.company_name || !form.contact_name || !form.email || !form.phone || !form.team_size || !form.preferred_dates) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/team-inquiries`, {
        ...form,
        team_size: parseInt(form.team_size) || 1
      });
      if (response.data.success) {
        toast.success('🤝 Team building request sent!', {
          description: "We'll reach out shortly to plan your event."
        });
        setForm(initialForm);
        onOpenChange(false);
      } else {
        toast.error(response.data.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      toast.error('Failed to send request', {
        description: 'Please try again later or contact us directly.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="team-inquiry-dialog">
        <DialogHeader>
          <DialogTitle>🤝 Team Building Request</DialogTitle>
          <DialogDescription>
            Tell us about your team and we'll put together the perfect paint-splattering experience.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Company Name *</label>
              <Input placeholder="Your company" value={form.company_name} onChange={update('company_name')} data-testid="team-company-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Contact Name *</label>
              <Input placeholder="Your name" value={form.contact_name} onChange={update('contact_name')} data-testid="team-contact-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Email *</label>
              <Input type="email" placeholder="you@company.com" value={form.email} onChange={update('email')} data-testid="team-email-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Phone *</label>
              <Input type="tel" placeholder="(701) 123-4567" value={form.phone} onChange={update('phone')} data-testid="team-phone-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Team Size *</label>
              <Input type="number" min="1" placeholder="e.g., 12" value={form.team_size} onChange={update('team_size')} data-testid="team-size-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Preferred Dates *</label>
              <Input placeholder="e.g., Sept 10-12 or Fridays" value={form.preferred_dates} onChange={update('preferred_dates')} data-testid="team-dates-input" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Anything else we should know?</label>
            <Textarea placeholder="Goals, occasion, budget range..." rows={3} value={form.message} onChange={update('message')} data-testid="team-message-input" />
          </div>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-600 hover:to-sky-600 text-white font-bold"
            data-testid="team-submit-button"
          >
            {submitting ? 'Sending...' : 'Send Team Building Request 🎨'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
